<script lang="ts" setup>
import { useShortCodesStore } from '../../_runtime/stores/shortcodes';
import type { ListItemModel, FilterModel } from '../../_runtime/services/ShortCodesService';
import ShortCodesService from '../../_runtime/services/ShortCodesService';

const route = useRoute();
const store = useShortCodesStore();
const $attrs = useAttrs();
const { $log } = useLogging();

const currentProperties = (): Record<string, any> => only($attrs, 'class', 'style', 'properties', 'dynamicForm', 'editShortCodeId');
const routeKey = ref('');
const items = ref<Resource<ListItemModel>[]>([]);
const permissions = ref({
    canCreate: false
});
const filter = computed<FormType<FilterModel>>(() => store.filter as any);
const pager = computed<Pager>(() => store.pager as any);

const emit = defineEmits<{
    (e: 'active', id: number, formOriginId: number): void;
    (e: 'edit', id: number): void;
    (e: 'closeModal', id: number, name: string, formId: number, typeId: number): void;
}>();

const loadData = async (): Promise<void> =>
{
    pager.value.loading = true;

    try
    {
        const response = await ShortCodesService.list(filter.value.data(), pager.value);

        pager.value.setTotalRows(response.totalRows);
        permissions.value = response.meta;
        items.value = response.items;
    }
    catch (ex)
    {
        pager.value.setTotalRows(0);
        items.value = [];
        $log.debug(ex);
    }
    finally
    {
        persistState(routeKey.value, filter.value, pager.value);
        pager.value.loading = false;
    }
};

onBeforeMount(async (): Promise<void> =>
{
    routeKey.value = route.name.toString();
    restoreState(routeKey.value, filter.value, pager.value);
    await loadData();
});

const handleClickOpenDetails = (id: number, formOriginId: number): void =>
{
    emit('active', id, formOriginId);
};
</script>

<template>
    <div class="shortcode-types-list" v-bind="currentProperties()">
        <button
            v-for="{ result: { name, id, formOriginId, icon, grid } } in items"
            :key="`shortcode-${id}`"
            class="btn btn-text-normal btn-light p-0 me-0 text-start shortcode-type"
            @click.stop.prevent="handleClickOpenDetails(id, formOriginId)"
        >
            <div class="w-100 type">
                <div class="me-3 type-icon">
                    <i :class="['fa-2x', icon]" />
                </div>
                <span class="fw-normal">{{ name }}</span>
            </div>
            <div class="shortcode-grid-container">
                <div v-for="(row, rowIndex) in grid" :key="rowIndex" class="grid-row">
                    <div
                        v-for="(column, colIndex) in row"
                        :key="colIndex"
                        class="grid-column"
                        :class="{ 'selected': column === 1 }"
                    />
                </div>
            </div>
        </button>
    </div>
</template>

<style scoped>
.shortcode-types-list {
    display: flex;
    gap: 20px;
    margin: 16px 0;
    flex-wrap: wrap;
    flex-direction: row;
    width: 100%;
}
.shortcode-grid-container {
    width: fit-content;
    padding: 10px;
    margin-top: auto;
}
.shortcode-grid-container .grid-row {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    width: 100%;
}
.shortcode-grid-container .grid-row .grid-column {
    display: flex;
    align-items: center;
    justify-content: center;
    height: .625rem;
    width: .625rem;
    background-color: var(--bs-gray-200);
    cursor: pointer;
}
.shortcode-grid-container .grid-row .grid-column.selected {
    background-color: var(--bs-white) !important;
}
.shortcode-grid-container .grid-row .grid-column {
    background-color: var(--ideo-grid-accent);
    pointer-events: none;
}
.type {
    padding: 16px 10px 0;
    line-height: 1.1;
    clear: both;
}
.type-icon {
    float: left;
    margin-top: -6px;
}
.shortcode-type {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-width: 150px;
    width: 100%;
}
.shortcode-type:hover .shortcode-grid-container .grid-row .grid-column {
    background-color: var(--ideo-grid-accent);
}
</style>
